<script setup lang="ts">
import type { BlobObject } from '../../types/gallery'
import { decodeImageSlug } from '../utils/url.ts'

const thumbnails = ref<HTMLUListElement>()
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

const router = useRouter()
const { images } = useFile()
const { width } = useWindowSize()
const isDetailRoute = computed(() => router.currentRoute.value.fullPath.includes('/detail/'))
const thumbnailBarCollapsed = useState<boolean>('detail-thumbnail-bar-collapsed', () => false)
const expandedHeight = 68
const collapsedHeight = 10

const thumbnailBarStyle = computed(() => ({
  height: `${thumbnailBarCollapsed.value ? collapsedHeight : expandedHeight}px`,
  transform: `translateY(${thumbnailBarCollapsed.value ? -(expandedHeight - collapsedHeight) : 0}px)`
}))

const canShowScrollButtons = computed(() => !thumbnailBarCollapsed.value && !!images.value?.length)

const thumbnailsListClass = computed(() =>
  thumbnailBarCollapsed.value
    ? 'opacity-0 pointer-events-none'
    : 'opacity-100 pointer-events-auto'
)

function updateScrollButtons() {
  const list = thumbnails.value
  if (!list) {
    canScrollLeft.value = false
    canScrollRight.value = false
    return
  }

  const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth)
  canScrollLeft.value = list.scrollLeft > 1
  canScrollRight.value = list.scrollLeft < maxScrollLeft - 1
}

async function updateBoundsAndClamp() {
  await nextTick()
  updateScrollButtons()
}

function scrollThumbnails(direction: 'left' | 'right') {
  const list = thumbnails.value
  if (!list) {
    return
  }

  const step = Math.max(180, Math.floor(width.value * 0.2))
  list.scrollBy({
    left: direction === 'left' ? -step : step,
    behavior: 'smooth'
  })

  requestAnimationFrame(updateScrollButtons)
  setTimeout(updateScrollButtons, 220)
}

async function moveThumbnail(slug: string) {
  if (!images.value?.length || !slug) {
    return
  }

  const decodedSlug = decodeImageSlug(slug)

  // get width of current image
  const visibleImages = images.value.filter((image: BlobObject) => !image.isUploading)
  const index = visibleImages.findIndex((image: BlobObject) => image.pathname === decodedSlug || image.pathname.endsWith(`/${decodedSlug}`))

  if (index < 0) {
    return
  }

  const list = thumbnails.value
  const imgToMove = ref<HTMLElement | undefined>(list?.children[index] as HTMLElement | undefined)

  if (!imgToMove.value || !list) {
    return
  }

  const imageWidth = imgToMove.value.offsetWidth
  const target = imgToMove.value.offsetLeft - ((list.clientWidth - imageWidth) / 2)
  const maxScrollLeft = Math.max(0, list.scrollWidth - list.clientWidth)
  const clampedTarget = Math.min(Math.max(0, target), maxScrollLeft)

  list.scrollTo({ left: clampedTarget, behavior: 'smooth' })

  requestAnimationFrame(updateScrollButtons)
  setTimeout(updateScrollButtons, 220)
}

// move thumbnail on mounted (if not gallery page)
onMounted(async () => {
  await nextTick()

  await updateBoundsAndClamp()

  if (!router.currentRoute.value.fullPath.includes('/detail/')) {
    return
  }

  const mountedSlugParam = router.currentRoute.value.params.slug
  const mountedSlug = Array.isArray(mountedSlugParam) ? mountedSlugParam[0] : undefined

  if (mountedSlug) {
    moveThumbnail(mountedSlug)
  }
})

// move thumbnail after route changes
router.afterEach(async (to, _) => {
  await nextTick()

  if (!router.currentRoute.value.fullPath.includes('/detail/')) {
    return
  }

  const slugParam = to.params.slug
  const slug = Array.isArray(slugParam) ? slugParam[0] : undefined

  if (slug) {
    moveThumbnail(slug)
  }
})

watch(width, async () => {
  await updateBoundsAndClamp()
})

watch(() => images.value?.length || 0, async () => {
  await updateBoundsAndClamp()
})
</script>

<template>
  <div
    v-if="isDetailRoute"
    class="group bg-black/10 backdrop-blur-md top-0 absolute overflow-hidden w-screen transition-all duration-300"
    :style="thumbnailBarStyle"
  >
    <ul
      v-if="images && images.length"
      ref="thumbnails"
      class="fixed top-2 left-0 right-0 whitespace-nowrap overflow-x-auto transition-opacity duration-200 px-4"
      :class="thumbnailsListClass"
      @scroll.passive="updateScrollButtons"
    >
      <ImageThumbnail
        v-for="(thumbnail, index) in images.filter(image => !image.isUploading)"
        :key="index"
        class="transform-gpu transition-transform duration-300 mx-4"
        :thumbnail="thumbnail"
      />
    </ul>

    <div
      v-if="canShowScrollButtons"
      class="pointer-events-none absolute inset-y-0 left-0 right-0 z-20 hidden md:block"
    >
      <div class="absolute inset-y-0 left-0 flex items-center px-2">
        <UButton
          v-if="canScrollLeft"
          icon="i-heroicons-chevron-left-20-solid"
          color="neutral"
          variant="soft"
          size="xs"
          class="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Scroll thumbnails left"
          @click="scrollThumbnails('left')"
        />
      </div>
      <div class="absolute inset-y-0 right-0 flex items-center px-2">
        <UButton
          v-if="canScrollRight"
          icon="i-heroicons-chevron-right-20-solid"
          color="neutral"
          variant="soft"
          size="xs"
          class="pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Scroll thumbnails right"
          @click="scrollThumbnails('right')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
ul {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

ul::-webkit-scrollbar {
  display: none;
}
</style>
